import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Stack,
  Divider,
  Paper,
  IconButton,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import { Upload, Save, Close, Download, ExpandMore, InsertDriveFile } from '@mui/icons-material';
import * as XLSX from 'xlsx';
import * as ExcelJS from 'exceljs';
import { useDispatch } from 'react-redux';
import { unwrapResult } from '@reduxjs/toolkit';
import { ThunkDispatch } from 'redux-thunk';
import { AnyAction } from 'redux';
import { useAppSelector } from 'app/hooks';
import {
  uploadPeopleDetailsFile,
  AddPeoplePayload,
  listScreenings
} from '../../../slices/payrolslices/PeopleSlice';
import { RootState } from 'app/store';

interface ImportFileProps {
  importedFile: File | null;
  onDrop: (e: React.DragEvent<HTMLDivElement>) => void;
  onDragOver: (e: React.DragEvent<HTMLDivElement>) => void;
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onClick: () => void;
  isSmallScreen: boolean;
  getFontSize: (small: string, medium: string, large: string) => string;
}

interface RawRecord {
  [key: string]: string | number | undefined;
}

interface UploadResults {
  message: string;
  summary: {
    total_processed: number;
    successful_inserts: number;
    users_not_in_approved_members: number;
    users_already_registered: number;
    reactivated_employees: number;
    failed_employees: number;
  };
  details: {
    successful?: Array<{ user_info: any }>;
    users_not_in_approved_members?: Array<{ user_info: any }>;
    users_already_registered?: Array<{ user_info: any }>;
    reactivated?: Array<{ user_info: any }>;
    failed?: Array<{ user_info: any; reason: string }>;
  };
}

const ImportFile: React.FC<ImportFileProps> = ({
  importedFile,
  onDrop,
  onDragOver,
  onFileChange,
  onClick,
  isSmallScreen,
  getFontSize
}) => {
  const dispatch = useDispatch<ThunkDispatch<RootState, unknown, AnyAction>>();
  const selectedProjectId = useAppSelector((state) => state.homeTilesServices.selectedProjectId);
  const selectedServiceTitle = useAppSelector(
    (state) => state.serviceSelected.selectedServiceTitle
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [uploadResults, setUploadResults] = useState<UploadResults | null>(null);
  const [showResults, setShowResults] = useState(false);

  /* ------------------------------------------------------------------ */
  /*                  EXCEL TEMPLATE - EXACT 35 COLUMNS                */
  /* ------------------------------------------------------------------ */
  const downloadExactEmployeeTemplate = async () => {
    try {
      setLoading(true);

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Employees');

      // Define dropdown options matching your Excel file
      const dropdownOptions: { [key: string]: string[] } = {
        State: ['NSW', 'VIC', 'QLD', 'WA', 'SA', 'TAS', 'ACT', 'NT'],
        'Payment option': ['Bank Transfer', 'Cash', 'Cheque'],
        'Employment type': ['Full Time', 'Part Time', 'Casual', 'Contract'],
        'Pay rate per': ['per Hour', 'per Day', 'per Week', 'per Month', 'per Year'],
        'Status of residency': ['Australian Resident', 'Foreign Resident', 'Working Holiday Maker'],
        'Claim tax free threshold': ['Yes', 'No'],
        'Has HELP or TSL debt': ['Yes', 'No'],
        'Has student Start-up loan': ['Yes', 'No'],
        'Has Financial Supplement Debt': ['Yes', 'No'],
        'Student Loans': ['Yes', 'No'],
        Shearer: ['Yes', 'No'],
        'WHM Registered': ['Yes', 'No'],
        'Income Type': [
          'Salary and Wages',
          'Allowances',
          'Directors Fees',
          'ETP',
          'Gross Interest',
          'Dividends'
        ]
      };

      // EXACT headers from your Excel file (35 columns)
      const headers = [
        'First name',
        'Last name',
        'Job title',
        'Email address',
        'DOB (dd/mm/yyyy)',
        'Address 1',
        'Address 2',
        'Suburb',
        'Postcode',
        'State',
        'Payment option',
        'Bank account name',
        'BSB',
        'Account number',
        'Superannuation USI',
        'Superannuation member number',
        'Super contribution rate',
        'Start date (dd/mm/yyyy)',
        'Employment type',
        'Weekly work hours',
        'Pay rate',
        'Pay rate per',
        'Status of residency',
        'TFN',
        'Claim tax free threshold',
        'Has HELP or TSL debt',
        'Has student Start-up loan',
        'Has Financial Supplement Debt',
        'Opening Annual leave balance 2',
        'Opening Sick leave balance',
        'Student Loans',
        'Shearer',
        'WHM Registered',
        'WHM Country',
        'Income Type'
      ];

      // Create header row
      const headerRow = worksheet.addRow(headers);

      // Example data row (corrected from your Excel)
      const exampleRow = [
        'Example',
        'Row',
        'Truck Driver',
        'stan@payroller.com.au',
        '12/11/2022',
        '44 Fake St',
        'Apartment 4',
        'Townsville',
        '3000',
        'NSW',
        'Cash',
        'Westpac',
        '123456',
        '123456789',
        'STA0100AU',
        '123456789',
        '10',
        '12/11/2022',
        'Full Time',
        '38',
        '50',
        'per Hour',
        'Australian Resident',
        '151994330',
        'Yes',
        'Yes',
        'No',
        'No',
        '',
        '',
        'No',
        'No',
        'No',
        'GB',
        'Salary and Wages'
      ];
      worksheet.addRow(exampleRow);

      // Add only 10 empty rows (not 200!)
      for (let i = 0; i < 10; i++) {
        worksheet.addRow(Array(headers.length).fill(''));
      }

      // Set column properties
      worksheet.columns = headers.map((header, index) => ({
        header: header,
        key: header.toLowerCase().replace(/[^a-z0-9]/g, '_'),
        width:
          [
            15, 15, 20, 30, 15, 25, 20, 15, 10, 8, 15, 20, 10, 15, 15, 20, 15, 15, 15, 15, 10, 12,
            20, 12, 15, 15, 18, 20, 20, 20, 12, 10, 15, 12, 18
          ][index] || 15
      }));

      // Apply dropdowns only to the limited number of rows
      Object.entries(dropdownOptions).forEach(([fieldName, options]) => {
        const colIndex = headers.indexOf(fieldName) + 1;
        if (colIndex > 0) {
          for (let row = 3; row <= 12; row++) {
            const cell = worksheet.getCell(row, colIndex);
            cell.dataValidation = {
              type: 'list',
              allowBlank: true,
              formulae: [`"${options.join(',')}"`],
              showInputMessage: true,
              promptTitle: fieldName,
              prompt: `Select from: ${options.join(', ')}`,
              showErrorMessage: true,
              errorStyle: 'error',
              errorTitle: 'Invalid Selection',
              error: `Please choose from the dropdown list`
            };
          }
        }
      });

      // Style header
      headerRow.font = { bold: true, color: { argb: 'FFFFFF' } };
      headerRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4472C4' } };
      headerRow.alignment = { horizontal: 'center', vertical: 'middle' };
      headerRow.height = 25;

      // Add borders and highlighting
      for (let rowNumber = 1; rowNumber <= 12; rowNumber++) {
        const row = worksheet.getRow(rowNumber);
        for (let colNumber = 1; colNumber <= headers.length; colNumber++) {
          const cell = row.getCell(colNumber);
          cell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
          };

          const header = headers[colNumber - 1];
          if (header && Object.keys(dropdownOptions).includes(header) && rowNumber > 1) {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF2CC' } };
          }
        }
      }

      worksheet.views = [{ state: 'frozen', ySplit: 1 }];

      // Generate file
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Employee_Import_Template_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setSuccess(true);
      setLoading(false);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error('Error creating template:', err);
      setError('Failed to generate template. Please try again.');
      setLoading(false);
    }
  };

  /* ------------------------------------------------------------------ */
  /*                    FILE PROCESSING - ONLY VALID ROWS              */
  /* ------------------------------------------------------------------ */
  const processFile = async (file: File) => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(false);
      setUploadResults(null);
      setShowResults(false);

      const ext = file.name.split('.').pop()?.toLowerCase();
      if (!['csv', 'xlsx', 'xls'].includes(ext || '')) {
        throw new Error('Unsupported file type. Please upload a .csv, .xlsx, or .xls file.');
      }

      let workbook: XLSX.WorkBook;
      if (ext === 'csv') {
        const csvText = await file.text();
        workbook = XLSX.read(csvText, { type: 'string' });
      } else {
        const data = new Uint8Array(await file.arrayBuffer());
        workbook = XLSX.read(data, { type: 'array' });
      }

      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonRows: RawRecord[] = XLSX.utils.sheet_to_json(sheet, {
        defval: '',
        blankrows: false
      });

      console.log('✅ Parsed JSON rows:', jsonRows.length);

      // **CRITICAL FIX: Filter out rows without required fields**
      const validRows = jsonRows.filter((row) => {
        const firstName = String(row['First name'] || '').trim();
        const lastName = String(row['Last name'] || '').trim();
        const email = String(row['Email address'] || '').trim();

        return (
          firstName &&
          lastName &&
          email &&
          firstName !== 'First name' &&
          firstName !== '' &&
          lastName !== '' &&
          email !== '' &&
          email.includes('@') // Basic email validation
        );
      });

      console.log(
        `✅ Valid employee rows: ${validRows.length} out of ${jsonRows.length} total rows`
      );

      if (validRows.length === 0) {
        throw new Error(
          'No valid employee records found. Please ensure you have filled in First name, Last name, and Email address for each employee.'
        );
      }

      // Transform ONLY valid rows into the required payload structure
      const transformedData = validRows.map((row) => ({
        personal: {
          firstName: row['First name'] || '',
          lastName: row['Last name'] || '',
          email: row['Email address'] || '',
          phone: '',
          gender: '',
          address1: row['Address 1'] || '',
          address2: row['Address 2'] || '',
          suburb: row['Suburb'] || '',
          state: row['State'] || '',
          postcode: row['Postcode'] ? String(row['Postcode']) : ''
        },
        bankAndSuper: {
          paymentMethod: row['Payment option'] || '',
          accountName: row['Bank account name'] || '',
          bank: '',
          bsb: row['BSB'] ? String(row['BSB']) : '',
          accountNumber: row['Account number'] ? String(row['Account number']) : '',
          superFundType: '',
          superContributionRate: row['Super contribution rate']
            ? String(row['Super contribution rate'])
            : '',
          superCompany: '',
          smsfsName: '',
          smsfsAbn: '',
          electronicServiceAddress: '',
          employerNumber: '',
          smsfsBankAccountName: '',
          smsfsBsb: '',
          smsfsAccountNumber: ''
        },
        employment: {
          jobTitle: row['Job title'] || '',
          startDate: row['Start date (dd/mm/yyyy)'] || '',
          employmentType: row['Employment type'] || '',
          weeklyWorkHours: row['Weekly work hours'] ? String(row['Weekly work hours']) : '',
          roles: '',
          payPeriod: '',
          leaveLoading: ''
        },
        payRates: {
          basePayRate: row['Pay rate'] ? String(row['Pay rate']) : '',
          basePayRateType:
            row['Pay rate per'] === 'per Hour'
              ? 'hour'
              : row['Pay rate per'] === 'per Day'
              ? 'day'
              : row['Pay rate per'] === 'per Year'
              ? 'year'
              : 'hour',
          saturdayRate: '',
          saturdayRateType: 'hour',
          sundayRate: '',
          sundayRateType: 'hour',
          publicHolidayRate: '',
          publicHolidayRateType: 'hour',
          additionalPublicHoliday: '',
          additionalPublicHolidayRateType: 'hour',
          nightShiftRate: '',
          nightShiftRateType: 'hour',
          nightShiftStartTime: '',
          nightShiftEndTime: '',
          holidays: []
        },
        tax: {
          dateOfBirth: row['DOB (dd/mm/yyyy)'] || '',
          statusOfResidency: row['Status of residency'] || '',
          incomeType: row['Income Type'] || '',
          tfnExemption: '',
          taxFileNumber: row['TFN'] ? String(row['TFN']) : '',
          claimTaxFreeThreshold: row['Claim tax free threshold'] === 'Yes',
          hasStudyAndTrainingLoan:
            row['Has HELP or TSL debt'] === 'Yes' ||
            row['Has student Start-up loan'] === 'Yes' ||
            row['Student Loans'] === 'Yes',
          isHorticulturistOrShearer: row['Shearer'] === 'Yes',
          hasChildSupportObligations: row['Has Financial Supplement Debt'] === 'Yes'
        },
        openingBalances: {
          asAtDate: '',
          leaveBalances: {
            annualLeaveBalance: Number(row['Opening Annual leave balance 2']) || 0,
            sickLeaveBalance: Number(row['Opening Sick leave balance']) || 0
          },
          ordinaryEarnings: {
            ordinaryTimeEarnings: 0,
            superGuarantee: 0,
            tax: 0,
            bonusesCommission: 0,
            directorsFee: 0,
            employerContributionsRESC: 0,
            superSalarySacrifice: 0,
            reimbursement: 0,
            overtime: 0
          },
          paidLeave: {
            annualLeave: 0,
            sickLeave: 0,
            longServiceLeave: 0,
            paidParentalLeave: 0,
            ancillaryAndDefenceLeave: 0,
            workersCompensation: 0
          },
          allowance: {
            awardTransportPayments: 0,
            centsPerKM: 0,
            laundry: 0,
            overtimeMeal: 0,
            qualifications: 0,
            task: 0,
            tool: 0,
            travel: 0,
            jobKeeperTopUp: 0,
            otherGeneral: 0,
            otherHomeOffice: 0,
            otherNonDeductible: 0,
            otherTransportFares: 0,
            otherUniform: 0,
            otherPrivateVehicle: 0
          },
          deductions: {
            preTaxFees: 0,
            preTaxWorkplaceGiving: 0,
            postTaxFees: 0,
            postTaxWorkplaceGiving: 0,
            childSupportGarnishees: 0,
            childSupportDeductions: 0
          },
          fringeBenefits: { fringeBenefitsTaxable: 0 }
        },
        template: {
          ordinaryTimeEarnings: { hours: 0, amount: 0 },
          earnings: [],
          extraHours: [],
          allowances: [],
          deductions: [],
          childSupport: [],
          superannuation: { employerContribution: 0, superSalarySacrifice: 0, isPercentage: true },
          fixedTaxAmount: { enabled: false, amount: 0 }
        },
        whm: {
          registered: row['WHM Registered'] === 'Yes',
          country: row['WHM Country'] || ''
        },
        superannuation: {
          usi: row['Superannuation USI'] || '',
          memberNumber: row['Superannuation member number']
            ? String(row['Superannuation member number'])
            : ''
        }
      }));

      console.log(`✅ Processing ${transformedData.length} valid employee records`);

      const payload: AddPeoplePayload = {
        group_id: selectedProjectId || '',
        service_id: selectedServiceTitle || '',
        user_info: transformedData
      };

      // Dispatch the uploadPeopleDetailsFile action
      const result = await dispatch(uploadPeopleDetailsFile(payload));

      try {
        const unwrappedResult = unwrapResult(result) as unknown as UploadResults;

        // Set results and show them
        setUploadResults(unwrappedResult);
        setShowResults(true);
        setSuccess(true);
        setError(null);

        console.log('✅ Upload successful:', unwrappedResult);

        // Refresh the employee list after successful upload
        if (selectedProjectId && selectedServiceTitle) {
          try {
            const listResult = await dispatch(
              listScreenings({
                group_id: selectedProjectId,
                service_id: selectedServiceTitle,
                page_size: 10
              })
            );
            unwrapResult(listResult);
            console.log('✅ Employee list refreshed');
          } catch (listError) {
            console.warn('Failed to refresh employee list:', listError);
            // Don't show error to user as the upload was successful
          }
        }

        // Hide results after 5 seconds
        setTimeout(() => {
          setShowResults(false);
          setUploadResults(null);
        }, 5000);
      } catch (uploadError) {
        console.error('Upload failed:', uploadError);
        const errorMessage =
          uploadError instanceof Error ? uploadError.message : 'Failed to upload employee data.';
        setError(errorMessage);
        setSuccess(false);
      }
    } catch (err) {
      console.error('Processing failed:', err);
      setError(err instanceof Error ? err.message : 'Failed to process the file.');
      setSuccess(false);
    } finally {
      setLoading(false);
    }
  };

  /* ------------------------------------------------------------------ */
  /*                    UPLOAD RESULTS DISPLAY                          */
  /* ------------------------------------------------------------------ */
  const UploadResultsDisplay = () => {
    if (!showResults || !uploadResults) return null;

    const { message, summary, details } = uploadResults;

    return (
      <Alert
        severity={summary?.successful_inserts > 0 ? 'success' : 'warning'}
        sx={{ mb: 2, fontSize: getFontSize('0.625rem', '0.6875rem', '0.75rem') }}
        onClose={() => setShowResults(false)}
      >
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
          📊 {message || 'Import Results Summary'}
        </Typography>
        {summary && (
          <Box sx={{ pl: 1, mb: 1 }}>
            <Typography variant="caption" component="div">
              • <strong>Total Processed:</strong> {summary.total_processed || 0}
            </Typography>
            <Typography variant="caption" component="div" color="success.main">
              • <strong>Successfully Imported:</strong> {summary.successful_inserts || 0}
            </Typography>
            {(summary?.reactivated_employees || 0) > 0 && (
              <Typography variant="caption" component="div" color="info.main">
                • <strong>Reactivated:</strong> {summary.reactivated_employees}
              </Typography>
            )}
            {(summary?.users_already_registered || 0) > 0 && (
              <Typography variant="caption" component="div" color="warning.main">
                • <strong>Already Registered:</strong> {summary.users_already_registered}
              </Typography>
            )}
            {(summary?.users_not_in_approved_members || 0) > 0 && (
              <Typography variant="caption" component="div" color="warning.main">
                • <strong>Not in Approved Members:</strong> {summary.users_not_in_approved_members}
              </Typography>
            )}
            {(summary?.failed_employees || 0) > 0 && (
              <Typography variant="caption" component="div" color="error.main">
                • <strong>Failed:</strong> {summary.failed_employees}
              </Typography>
            )}
          </Box>
        )}

        {details && (
          <>
            {/* Successful Imports */}
            {details.successful && details.successful.length > 0 && (
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: 'success.main' }}>
                    ✅ Successful Imports ({details.successful.length})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                    {details.successful.slice(0, 5).map((item: any, index: number) => (
                      <Box
                        key={index}
                        sx={{ mb: 1, p: 1, backgroundColor: '#e6f4ea', borderRadius: 1 }}
                      >
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                          Employee: {item.user_info?.personal?.firstName || 'Unknown'}{' '}
                          {item.user_info?.personal?.lastName || ''}
                        </Typography>
                      </Box>
                    ))}
                    {details.successful.length > 5 && (
                      <Typography variant="caption" sx={{ fontStyle: 'italic' }}>
                        ... and {details.successful.length - 5} more successful imports
                      </Typography>
                    )}
                  </Box>
                </AccordionDetails>
              </Accordion>
            )}

            {/* Already Registered */}
            {details.users_already_registered && details.users_already_registered.length > 0 && (
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: 'warning.main' }}>
                    ⚠️ Already Registered ({details.users_already_registered.length})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                    {details.users_already_registered
                      .slice(0, 5)
                      .map((item: any, index: number) => (
                        <Box
                          key={index}
                          sx={{ mb: 1, p: 1, backgroundColor: '#fff3e0', borderRadius: 1 }}
                        >
                          <Typography variant="caption" sx={{ fontWeight: 600 }}>
                            Employee: {item.user_info?.personal?.firstName || 'Unknown'}{' '}
                            {item.user_info?.personal?.lastName || ''}
                          </Typography>
                        </Box>
                      ))}
                    {details.users_already_registered.length > 5 && (
                      <Typography variant="caption" sx={{ fontStyle: 'italic' }}>
                        ... and {details.users_already_registered.length - 5} more already
                        registered
                      </Typography>
                    )}
                  </Box>
                </AccordionDetails>
              </Accordion>
            )}

            {/* Reactivated Employees */}
            {details.reactivated && details.reactivated.length > 0 && (
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: 'info.main' }}>
                    🔄 Reactivated Employees ({details.reactivated.length})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                    {details.reactivated.slice(0, 5).map((item: any, index: number) => (
                      <Box
                        key={index}
                        sx={{ mb: 1, p: 1, backgroundColor: '#e6f4ea', borderRadius: 1 }}
                      >
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                          Employee: {item.user_info?.personal?.firstName || 'Unknown'}{' '}
                          {item.user_info?.personal?.lastName || ''}
                        </Typography>
                      </Box>
                    ))}
                    {details.reactivated.length > 5 && (
                      <Typography variant="caption" sx={{ fontStyle: 'italic' }}>
                        ... and {details.reactivated.length - 5} more reactivations
                      </Typography>
                    )}
                  </Box>
                </AccordionDetails>
              </Accordion>
            )}

            {/* Failed Imports */}
            {details.failed && details.failed.length > 0 && (
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: 'error.main' }}>
                    ❌ Failed Imports ({details.failed.length})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                    {details.failed.slice(0, 5).map((failedItem: any, index: number) => (
                      <Box
                        key={index}
                        sx={{ mb: 1, p: 1, backgroundColor: '#ffebee', borderRadius: 1 }}
                      >
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                          Employee: {failedItem.user_info?.personal?.firstName || 'Unknown'}{' '}
                          {failedItem.user_info?.personal?.lastName || ''}
                        </Typography>
                        <Typography variant="caption" component="div" color="error.main">
                          Reason: {failedItem.reason || 'Unknown error'}
                        </Typography>
                      </Box>
                    ))}
                    {details.failed.length > 5 && (
                      <Typography variant="caption" sx={{ fontStyle: 'italic' }}>
                        ... and {details.failed.length - 5} more failures
                      </Typography>
                    )}
                  </Box>
                </AccordionDetails>
              </Accordion>
            )}
          </>
        )}
      </Alert>
    );
  };

  /* ------------------------------------------------------------------ */
  /*                        UI EVENT HANDLERS                           */
  /* ------------------------------------------------------------------ */
  const handleImport = async () => {
    if (!importedFile) {
      setError('No file selected. Please upload a valid file.');
      return;
    }

    await processFile(importedFile);

    // Clear the file input after processing (regardless of success/failure)
    if (!error) {
      onFileChange({ target: { files: [] } } as any);
    }
  };

  const handleLocalDrop = (e: React.DragEvent<HTMLDivElement>) => {
    onDrop(e);
    setDragOver(false);
  };

  const handleLocalDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    onDragOver(e);
    setDragOver(true);
  };

  const handleLocalDragLeave = () => {
    setDragOver(false);
  };

  /* ------------------------------------------------------------------ */
  /*                              JSX                                   */
  /* ------------------------------------------------------------------ */
  return (
    <Box sx={{ p: isSmallScreen ? 2 : 3 }}>
      {/* Header */}
      <Stack direction="row" justifyContent="flex-end" alignItems="center" sx={{ mb: 2 }}>
        <Button
          variant="outlined"
          startIcon={loading ? <CircularProgress size={16} /> : <Download />}
          onClick={downloadExactEmployeeTemplate}
          disabled={loading}
          sx={{
            textTransform: 'none',
            fontSize: getFontSize('0.6875rem', '0.75rem', '0.875rem'),
            borderColor: '#217346',
            color: '#217346',
            '&:hover': { borderColor: '#1a5d37', backgroundColor: '#f0f8f0' }
          }}
        >
          {loading ? 'Generating...' : 'Download Excel Template'}
        </Button>
      </Stack>

      <Divider sx={{ mb: 2 }} />

      <Stack spacing={2}>
        <Typography variant="body2" color="text.secondary">
          In order to import employees, download and use the Excel template above.
        </Typography>

        {/* Upload Results Display */}
        <UploadResultsDisplay />

        {/* Success/Error Alerts */}
        {success && !showResults && (
          <Alert
            severity="success"
            sx={{ fontSize: getFontSize('0.625rem', '0.6875rem', '0.75rem') }}
          >
            {importedFile
              ? 'Employee data processed successfully!'
              : 'Excel template downloaded successfully!'}
          </Alert>
        )}
        {error && (
          <Alert
            severity="error"
            sx={{ fontSize: getFontSize('0.625rem', '0.6875rem', '0.75rem') }}
          >
            ❌ {error}
          </Alert>
        )}

        {/* Drag-and-drop zone */}
        <Paper
          elevation={dragOver ? 8 : 2}
          sx={{
            p: isSmallScreen ? 2 : 3,
            textAlign: 'center',
            border: `2px dashed ${dragOver ? '#1976d2' : '#bdbdbd'}`,
            bgcolor: dragOver ? '#e3f2fd' : 'background.paper',
            transition: 'all 0.2s ease',
            cursor: 'pointer'
          }}
          onDrop={handleLocalDrop}
          onDragOver={handleLocalDragOver}
          onDragLeave={handleLocalDragLeave}
          onClick={onClick}
        >
          <Stack spacing={2} alignItems="center">
            <Upload
              sx={{ fontSize: getFontSize('1.5rem', '2rem', '2.5rem'), color: 'text.secondary' }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
              {dragOver
                ? 'Drop your Excel file here'
                : 'Drag and drop your Excel file or click to upload'}
            </Typography>
            <Button
              variant="contained"
              component="label"
              startIcon={<Upload />}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Browse Files
              <input type="file" accept=".xlsx,.xls,.csv" hidden onChange={onFileChange} />
            </Button>
            <Typography variant="caption" color="text.secondary">
              Supports .xlsx, .xls, .csv files (Use template above for best results)
            </Typography>
            {importedFile && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  p: 2,
                  backgroundColor: '#f5f5f5',
                  borderRadius: 1,
                  mt: 2
                }}
              >
                <InsertDriveFile
                  sx={{ fontSize: getFontSize('1.25rem', '1.5rem', '1.75rem'), color: '#217346' }}
                />
                <Typography
                  variant="body2"
                  color="text.primary"
                  sx={{
                    fontSize: getFontSize('0.625rem', '0.6875rem', '0.75rem'),
                    fontWeight: 500
                  }}
                >
                  Selected: {importedFile.name}
                </Typography>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onFileChange({ target: { files: [] } } as any);
                  }}
                  sx={{ ml: 1 }}
                >
                  <Close fontSize="small" />
                </IconButton>
              </Box>
            )}
          </Stack>
        </Paper>

        {/* Footer buttons */}
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={<Close sx={{ fontSize: getFontSize('0.875rem', '1rem', '1rem') }} />}
            fullWidth
            onClick={() => onFileChange({ target: { files: [] } } as any)}
            disabled={loading}
            sx={{
              textTransform: 'none',
              fontSize: getFontSize('0.6875rem', '0.75rem', '0.875rem'),
              fontWeight: 500
            }}
          >
            Clear File
          </Button>
          <Button
            variant="contained"
            startIcon={
              loading ? (
                <CircularProgress size={16} sx={{ color: 'white' }} />
              ) : (
                <Save sx={{ fontSize: getFontSize('0.875rem', '1rem', '1rem') }} />
              )
            }
            fullWidth
            disabled={!importedFile || loading}
            onClick={handleImport}
            sx={{
              textTransform: 'none',
              fontSize: getFontSize('0.6875rem', '0.75rem', '0.875rem'),
              fontWeight: 600,
              backgroundColor: '#217346',
              '&:hover': { backgroundColor: '#1a5d37' }
            }}
          >
            {loading ? 'Processing...' : 'Import Employee Data'}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

export default ImportFile;
